/**
 * Auto-detect test mode and platform from W3C capabilities.
 *
 * Inspects `platformName`, `appium:app`, and `browserName` to determine
 * whether the session targets a native app, hybrid app, or mobile web.
 */

import type { AppTestMode, Platform, DetectedCapabilities } from './types.js';

export function detectTestMode(caps: Record<string, unknown>): AppTestMode {
  const app = caps['appium:app'] ?? caps['app'];
  const browserName = caps['browserName'];

  if (app && !browserName) return 'native';
  if (app && browserName) return 'hybrid';
  if (browserName && !app) return 'mobile-web';
  return 'native';
}

export function detectPlatform(caps: Record<string, unknown>): Platform {
  const raw = String(caps['platformName'] ?? '').toLowerCase();
  if (raw.includes('android')) return 'android';
  if (raw.includes('ios')) return 'ios';
  return 'unknown';
}

export function extractCapabilities(caps: Record<string, unknown>): DetectedCapabilities {
  return {
    platform: detectPlatform(caps),
    testMode: detectTestMode(caps),
    platformVersion: asStringOrNull(caps['appium:platformVersion'] ?? caps['platformVersion']),
    deviceName: asStringOrNull(caps['appium:deviceName'] ?? caps['deviceName']),
    automationName: asStringOrNull(caps['appium:automationName'] ?? caps['automationName']),
    appPath: asStringOrNull(caps['appium:app'] ?? caps['app']),
    browserName: asStringOrNull(caps['browserName']),
    udid: asStringOrNull(caps['appium:udid'] ?? caps['udid']),
  };
}

function asStringOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.length > 0) return value;
  return null;
}
